import React, { useRef, useState } from "react";
import ReCAPTCHA from 'react-google-recaptcha';
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import Axis_Logo from '../images/Axis_image6.png';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/home.css";
import "../styles/contact.css";

const RECAPTCHA_SITE_KEY = "6LdwstIpAAAAAL1_lBneLQEQRHpyUpxdO5ZXJ0R9";
const SUBMIT_EMAIL       = "yokaisoftware66@gmail.com"; // ← swap this

export default function Contact() {
  useScrollBehavior();

  const formRef      = useRef(null);
  const recaptchaRef = useRef(null);
  const [startTime]  = useState(Date.now());

  const [formData, setFormData] = useState({
    from_name: '',
    reply_to:  '',
    subject:   '',
    message:   '',
  });

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [status,         setStatus]         = useState("idle"); // idle | success | error

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setFormData({ from_name: '', reply_to: '', subject: '', message: '' });
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (Date.now() - startTime < 3000) {
      alert('Submission too fast.');
      return;
    }

    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA.');
      return;
    }

    setIsSubmitting(true);

    try {
      const submission = new FormData();
      submission.append('name',       formData.from_name);
      submission.append('email',      formData.reply_to);
      submission.append('subject',    formData.subject);
      submission.append('message',    formData.message);
      submission.append('_subject',   `Axis Contact — ${formData.subject}`);
      submission.append('_template',  'table');
      submission.append('_captcha',   'false');
      submission.append('_replyto',   formData.reply_to);
      submission.append('_next',      window.location.href);

      const response = await fetch(
        `https://formsubmit.co/ajax/${SUBMIT_EMAIL}`,
        { method: 'POST', headers: { Accept: 'application/json' }, body: submission }
      );

      const result = await response.json();

      if (result.success === 'true' || response.ok) {
        setStatus("success");
        resetForm();
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="home">
      <PageSEO
        title="Contact | The Axis"
        description="Get in touch with the Axis organization."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact The Axis",
          "url": (process.env.REACT_APP_SITE_URL || "") + "/contact"
        }}
      />

      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">
        <div className="module contact__module">

          {/* ── LEFT ── */}
          <div className="contact__left">
            <img src={Axis_Logo} alt="Axis logo" className="contact__logo" />
            <h1 className="contact__heading">Get in Touch</h1>
            <p className="contact__left-body">
              Questions about the philosophy, the book, membership,
              or anything else — send a message and we will reply by email.
            </p>
            <div className="contact__meta">
              <div className="contact__meta-row">
                <span className="contact__meta-label">Organization</span>
                <span className="contact__meta-value">The Axis</span>
              </div>
              <div className="contact__meta-row">
                {/*<span className="contact__meta-label">Based in</span>
                <span className="contact__meta-value">Sarasota, Florida</span>*/}
              </div>
              <div className="contact__meta-row">
                <span className="contact__meta-label">Response time</span>
                <span className="contact__meta-value">Within 48 hours</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className="contact__right">
            {status === "success" ? (
              <div className="contact__success">
                <h2 className="contact__success-heading">Message Sent</h2>
                <p className="contact__success-body">
                  We have received your message and will reply to your email shortly.
                </p>
                <button
                  className="btn btn--ghost"
                  onClick={() => setStatus("idle")}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                className="contact__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="contact__field">
                  <label className="contact__label" htmlFor="c-name">Name</label>
                  <input
                    id="c-name"
                    className="contact__input"
                    type="text"
                    name="from_name"
                    value={formData.from_name}
                    onChange={handleChange}
                    placeholder="Your name"
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="contact__field">
                  <label className="contact__label" htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    className="contact__input"
                    type="email"
                    name="reply_to"
                    value={formData.reply_to}
                    onChange={handleChange}
                    placeholder="you@domain.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="contact__field">
                  <label className="contact__label" htmlFor="c-subject">Subject</label>
                  <select
                    id="c-subject"
                    className="contact__input contact__select"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>Select a topic</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="The Book">The Book</option>
                    <option value="Join the Axis">Join the Axis</option>
                    <option value="The Platform">The Platform</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="contact__field">
                  <label className="contact__label" htmlFor="c-message">Message</label>
                  <textarea
                    id="c-message"
                    className="contact__input contact__textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    rows={6}
                    required
                  />
                </div>

                {/* Honeypot */}
                <input type="text" name="_honey" style={{ display: 'none' }} />
                <input type="hidden" name="_captcha" value="false" />

                {status === "error" && (
                  <p className="contact__error">
                    Something went wrong. Please try again.
                  </p>
                )}

                <div className="contact__captcha">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={token => setRecaptchaToken(token || '')}
                    theme="dark"
                  />
                </div>

                <div className="contact__actions">
                  <button
                    className="btn btn--primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending…" : "Send Message"}
                  </button>
                  <p className="contact__fine-print">
                    Do not include sensitive personal information.
                  </p>
                </div>
              </form>
            )}
          </div>

        </div>
      </section>
    </main>
  );
}



/*import React from "react";
import PageSEO from "../components/seo/PageSEO";
import "../styles/contact.css";
import { useScrollBehavior } from './useScrollBehavior.js'; 

export default function Contact() {

  useScrollBehavior();

  return (
    <main className="page">
      <PageSEO
        title="Contact | Your App Name"
        description="Contact the team."
        path="/contact"
      />

      <header className="pageHeader">
        <h1>Contact</h1>
        <p>Send a message. We'll reply by email.</p>
      </header>

      <section className="card">
        <form className="form" onSubmit={(e) => e.preventDefault()}>
          <label>
            <span>Name</span>
            <input type="text" name="name" placeholder="Your name" autoComplete="name" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" name="email" placeholder="you@domain.com" autoComplete="email" />
          </label>
          <label>
            <span>Message</span>
            <textarea name="message" placeholder="What can we help with?" rows={6} />
          </label>

          <div className="formRow">
            <button className="btn btnPrimary" type="submit">Send</button>
            <a className="btn btnGhost" href="/book">Buy the Book</a>
          </div>

          <p className="finePrint">
            By sending, you agree not to include sensitive personal information.
          </p>
        </form>
      </section>
    </main>
  );
}*/
